/**
 * Engine-internal types: actions a player can submit and engine-level errors.
 *
 * The "public" game-state types live in `../types.ts`. This file is just the
 * vocabulary of player actions plus the error class the engine throws on
 * illegal moves.
 */

import type { RacingColor } from '../types'

/**
 * A single player action. Camel Up gives each player exactly one of these
 * choices on their turn:
 *
 *   - `take-pyramid-ticket` — draw a die from the pyramid at random; the
 *     selected camel moves; +1 coin (credited at leg end).
 *   - `take-leg-bet`        — claim the next available leg-bet ticket of a
 *                             specific racing color.
 *   - `place-race-bet`      — place one of your race-bet cards on the
 *                             winner or loser pile (face-down).
 *
 * In addition, `continue-leg` is a "system" action triggered to dismiss the
 * leg-end coin-review screen and roll into the next leg. Any player may
 * submit it (the UI typically presents it on the TV / host view).
 */
export type Move =
  | {
      kind: 'take-pyramid-ticket'
      uid: string
    }
  | {
      kind: 'take-leg-bet'
      uid: string
      color: RacingColor
    }
  | {
      kind: 'place-race-bet'
      uid: string
      pile: 'winner' | 'loser'
      color: RacingColor
    }
  | {
      kind: 'continue-leg'
      uid: string
    }

/** Error thrown by `applyMove` when validation fails. */
export class EngineError extends Error {
  constructor(
    public readonly code: EngineErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'EngineError'
  }
}

export type EngineErrorCode =
  | 'WRONG_STATUS'
  | 'NOT_YOUR_TURN'
  | 'UNKNOWN_PLAYER'
  | 'GAME_OVER'
  | 'NO_TICKETS_LEFT'
  | 'PYRAMID_EMPTY'
  | 'CARD_NOT_AVAILABLE'
  | 'INVALID_COLOR'
